import {
  Box,
  IconButton,
  MenuItem,
  Select as MuiSelect,
  Popover,
  SvgIcon,
  Typography,
  useMediaQuery,
} from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { format } from 'date-fns'
import { getSiteDateFormat } from '@hapana/shared/web/utils/date-format-utils'
import { t } from 'i18next'

import ClearSearchIcon from '@hapana/shared/web/styles/assets/svgs/times-circle.svg'
import DatePicker from '../../../../../components/shared/date-picker/date-picker.component'
import { VARIANTS } from '../../../../../components/shared/date-picker/date-picker.constants'
import Loading from '../../../../../components/shared/loading/loading.component'
import { commonStyles } from '../../../../../styles/common.styles'

import { handleStylesWithProps, styles } from '../../table.styles'
import { FILTER_DEFAULT_VALUE, FILTER_SELECT_TYPES } from '../../table.constants'

const TableFilterSelect = ({
  name,
  options,
  placeholder,
  filterStyles,
  type = FILTER_SELECT_TYPES.select,
  isFiltersLoading,
  muiSelectProps,
  filterContainerStyles,
  initialValue,
  disabled,
  displayEmpty = true,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const [anchorEl, setAnchorEl] = useState(null)
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const { control } = useFormContext()
  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
    defaultValue: initialValue || FILTER_DEFAULT_VALUE,
  })

  // Filter out invalid options
  const validOptions = (options || []).filter(
    (option) => option && option.value !== undefined && option.value !== null
  )

  const stylesWithProps = handleStylesWithProps({
    filterSelectType: type,
    disabled,
  })

  const renderValue = useCallback(
    (value) => {
      if (!value && value !== false) {
        return (
          <Typography
            typography="header4"
            sx={[styles.filterSelect.placeholder, stylesWithProps.filterSelect.select.placeholder]}
          >
            {placeholder}
          </Typography>
        )
      }

      return (
        <Box sx={styles.flexSpaceBetween}>
          <Box sx={styles.filterSelect.renderValue} maxWidth={50}>
            <Typography typography="header5" sx={styles.filterSelect.selectedPlaceholder}>
              {placeholder}
            </Typography>
            <Typography
              typography="header4"
              sx={styles.filterSelect.value}
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {type === FILTER_SELECT_TYPES.date
                ? `${value[0] ? format(value[0], getSiteDateFormat('medium')) : ''} -
                    ${value[1] ? format(value[1], getSiteDateFormat('medium')) : ''}`
                : validOptions.find((option) => option.value === value)?.label || value}
            </Typography>
          </Box>
        </Box>
      )
    },
    [placeholder, validOptions, type, stylesWithProps]
  )

  const handleOpen = (event) => {
    if (type === FILTER_SELECT_TYPES.select) {
      setIsSelectOpen(true)
    } else {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleClose = () => {
    if (type === FILTER_SELECT_TYPES.select) {
      setIsSelectOpen(false)
    } else {
      setAnchorEl(null)
    }
  }

  const handleChange = (e) => {
    if (type === FILTER_SELECT_TYPES.select) {
      onChange(e.target.value)
      setIsSelectOpen(false)
    }
  }

  const isOpen = Boolean(anchorEl)
  const id = isOpen ? 'simple-popover' : undefined
  const isLoadingFilters = isFiltersLoading && options.length === 0

  return (
    <Box sx={[styles.filterSelect.root, filterContainerStyles]}>
      <MuiSelect
        onChange={handleChange}
        value={value}
        sx={[
          styles.filterSelect[isMobile ? 'mobileSelect' : 'desktopSelect'],
          stylesWithProps.filterSelect.select,
          filterStyles,
        ]}
        test-id={`table-filter-select-MuiSelect`}
        renderValue={renderValue}
        open={isSelectOpen}
        onOpen={handleOpen}
        aria-describedby={id}
        onClose={handleClose}
        MenuProps={muiSelectProps}
        disabled={disabled}
        displayEmpty={displayEmpty}
      >
        {!isLoadingFilters && <MenuItem value={''}>{placeholder}</MenuItem>}
        {isLoadingFilters ? (
          <MenuItem disabled>
            <Box sx={[commonStyles.fullWidth, commonStyles.centeredFlexRow]}>
              <Loading label={t('common:components.table.loading')} />
            </Box>
          </MenuItem>
        ) : (
          validOptions.map((option, idx) => (
            <MenuItem key={option.value} value={option.value} test-id={`${option.value}.${idx}`}>
              {option.label}
            </MenuItem>
          ))
        )}
      </MuiSelect>
      {value || value === false ? (
        <Box sx={styles.filterSelect.clearButtonContainer}>
          <IconButton
            onClick={() => onChange(FILTER_DEFAULT_VALUE)}
            size="small"
            sx={styles.filterSelect.clearButton}
          >
            <SvgIcon component={ClearSearchIcon} />
          </IconButton>
        </Box>
      ) : null}
      <Popover
        open={isOpen}
        id={id}
        test-id={`table-filter-select-popover`}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DatePicker variant={VARIANTS.RANGE} value={value} onChange={onChange} />
      </Popover>
    </Box>
  )
}

TableFilterSelect.propTypes = {
  name: PropTypes.string.isRequired,
  filterStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  filterContainerStyles: PropTypes.object,
  muiSelectProps: PropTypes.object,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(Object.values(FILTER_SELECT_TYPES)),
  isFiltersLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  initialValue: PropTypes.string,
  displayEmpty: PropTypes.bool,
}

TableFilterSelect.defaultProps = {
  options: [],
}

export default TableFilterSelect
